import { Check, PersonStanding } from "lucide-react";
import { BODY_SHAPE_OPTIONS } from "@/components/find-my-size/body-shape-options";

// ═══════════════════════════════════════════════════════════════════════════
// STEP: Vücut Ölçüsü — typography-forward, Lucide reference watermark
//
// Design (2026-08-17): Ditched stick-figure silhouettes. Each card has a
// single Lucide PersonStanding as a subtle body-reference watermark, a big
// typography label ("Dar" / "Normal" / "Geniş") that carries the meaning,
// and a minimal width-indicator bar that visually reinforces the variant.
// Editorial, quiet, premium — no naive figures.
// ═══════════════════════════════════════════════════════════════════════════
export const BodyStep = ({ kind, profile, onSelect, copy, accent, hideHeader = false }) => {
    const meta = BODY_SHAPE_OPTIONS[kind];
    if (!meta) return null;
    const selected = profile[kind];
    const customPrompt = copy.bodyPrompts?.[kind];

    return (
        <div className="space-y-6" data-testid={`fms-step-body-${kind}`}>
            {!hideHeader && (
                <div>
                    <h3 className="text-[24px] font-bold text-foreground tracking-tight leading-tight">
                        {customPrompt || meta.prompt}
                    </h3>
                    <p className="text-[13.5px] text-muted-foreground mt-2 leading-relaxed">
                        {copy.bodyHints?.[kind] || copy.bodyHint || "Sana en yakın olanı seç — mükemmel olmasına gerek yok."}
                    </p>
                </div>
            )}
            {/* Tek sütun — Fit kartı DNA'sı: p-4, iki satır text (label + hint), sağda radio */}
            <div className="space-y-2.5">
                {meta.options.map((opt, i) => {
                    const isSelected = selected === opt.value;
                    return (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => onSelect(opt.value)}
                            className="w-full h-20 flex items-center justify-between gap-4 rounded-lg border px-4 py-3 text-left transition-all sayz-fms-chip hover:border-foreground/30 hover:-translate-y-[1px] hover:shadow-[0_2px_10px_-2px_rgba(0,0,0,0.06)] active:scale-[0.99]"
                            style={{
                                ...(isSelected
                                    ? {
                                          borderColor: accent,
                                          background: `color-mix(in srgb, ${accent} 5%, transparent)`,
                                          boxShadow: `0 0 0 1px ${accent}`,
                                      }
                                    : { borderColor: "hsl(var(--border))" }),
                                animationDelay: `${i * 60}ms`,
                            }}
                            data-testid={`fms-body-opt-${kind}-${opt.value}`}
                        >
                            {/* Sol: PersonStanding minimal watermark + Fit hiyerarşisi (label + hint) */}
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                                <PersonStanding
                                    className="h-5 w-5 shrink-0 transition-colors"
                                    strokeWidth={1.5}
                                    style={{
                                        color: isSelected ? accent : "hsl(var(--muted-foreground))",
                                        opacity: isSelected ? 0.9 : 0.55,
                                    }}
                                    aria-hidden
                                />
                                <div className="min-w-0 flex-1">
                                    <div
                                        className="text-[15px] font-semibold leading-tight transition-colors"
                                        style={{ color: isSelected ? accent : "hsl(var(--foreground))" }}
                                    >
                                        {opt.label}
                                    </div>
                                    <div className="text-[12px] text-muted-foreground mt-0.5 leading-snug">
                                        {opt.hint}
                                    </div>
                                </div>
                            </div>
                            {/* Sağ: radio check — Fit adımıyla birebir */}
                            <span
                                className="h-5 w-5 rounded-full border-2 grid place-items-center shrink-0 transition-all"
                                style={{
                                    borderColor: isSelected ? accent : "hsl(var(--border))",
                                    background: isSelected ? accent : "transparent",
                                }}
                            >
                                {isSelected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};
